import classNames from "classnames"
import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"

import DataTableHeadRow from "src/components/DataTable/DataTableHeadRow"
import FormCheckbox from "src/components/Form/FormCheckbox"
import OverflowMenu from "src/components/OverflowMenu"
import Pagination from "src/components/Pagination"
import Table from "src/components/Table"

import { useLocalStorageState } from "src/hooks/use_local_storage_state"

import {
  SelectablePropType,
  deselectAll,
  getSelectedCount,
  isAllPageRowsSelected,
  isAllSelected,
  isRowSelected,
  selectAll,
  selectRows,
  toggleRowSelection,
} from "./selection"

const useManageTableColumns = ({ defaultColumns, name }) => {
  const [storedColumns, setStoredColumns] = useLocalStorageState(
    name,
    "columns"
  )

  const columns = defaultColumns.map((defaultColumn) => {
    const storedColumn = storedColumns?.find((column) =>
      Object.prototype.hasOwnProperty.call(column, defaultColumn.key)
    )

    return {
      ...defaultColumn,
      hide: storedColumn
        ? storedColumn[defaultColumn.key]
        : defaultColumn.hide || false,
    }
  })

  const updateColumns = (updatedColumns) => {
    const columnsToStore = updatedColumns.map(({ key, hide }) => ({
      [key]: hide,
    }))
    setStoredColumns(columnsToStore)
  }

  return [columns, updateColumns]
}

const DataTable = ({
  autoColumnWidth = false,
  colDefs,
  fullHeight = false,
  hideActions,
  hideSettings,
  infinite = false,
  isLoading = false,
  maxTableWidth = false,
  name,
  numberOfPages,
  sortConfig,
  onPageChange,
  page,
  pagination = false,
  rowConfig,
  rowCount,
  rowData,
  selectable,
  showBlankRows = false,
}) => {
  const addIdsToRows = (data) => {
    return data?.map((row, index) => {
      if (!row.id) {
        return { ...row, id: `row-${index}` }
      }
      return row
    })
  }

  const [columns, setColumns] = useManageTableColumns({
    defaultColumns: colDefs,
    name,
  })
  const [rows, setRows] = useState(addIdsToRows(rowData))

  useEffect(() => {
    setRows(addIdsToRows(rowData))
  }, [rowData])

  const visibleColumnsCount = columns.filter((colDef) => !colDef.hide).length

  // Selection logic
  const isSelectionEnabled = Boolean(selectable)
  const selection = selectable?.selection
  const onSelectionChange = selectable?.onSelectionChange
  const isRowSelectableFn = selectable?.isRowSelectable ?? (() => true)
  const totalSelectableCount = selectable?.totalSelectableCount ?? 0
  const allSelectableIds = selectable?.allSelectableIds ?? []
  const selectionActions = selectable?.actions ?? []

  // Get selectable row IDs for current page
  const pageSelectableIds =
    rows?.filter((row) => isRowSelectableFn(row)).map((row) => row.id) ?? []

  // Derived selection state
  const selectedCount = isSelectionEnabled ? getSelectedCount(selection) : 0
  const allPageRowsSelected =
    isSelectionEnabled && isAllPageRowsSelected(selection, pageSelectableIds)
  const allItemsSelected =
    isSelectionEnabled && isAllSelected(selection, allSelectableIds)
  const hasPagination = numberOfPages > 1
  // Show "Select all X items" banner when:
  // - Selection is enabled
  // - Multiple pages exist
  // - All items on current page are selected
  // - Not all items across pages are selected yet
  // - We have all IDs available (allSelectableIds provided)
  const showSelectAllBanner =
    isSelectionEnabled &&
    hasPagination &&
    allPageRowsSelected &&
    !allItemsSelected &&
    allSelectableIds.length > pageSelectableIds.length

  // Selection handlers
  const handleSelectAll = () => {
    if (!isSelectionEnabled) return

    if (hasPagination) {
      // Select current page only - adds to existing selection
      onSelectionChange(selectRows(selection, pageSelectableIds))
    } else {
      // No pagination - select all available IDs
      const idsToSelect =
        allSelectableIds.length > 0 ? allSelectableIds : pageSelectableIds
      onSelectionChange(selectAll(idsToSelect))
    }
  }

  const handleDeselectAll = () => {
    if (!isSelectionEnabled) return
    onSelectionChange(deselectAll())
  }

  const handleSelectAllAcrossPages = () => {
    if (!isSelectionEnabled) return
    // Select all IDs across all pages
    onSelectionChange(selectAll(allSelectableIds))
  }

  const handleToggleRowSelection = (rowId) => {
    if (!isSelectionEnabled) return
    onSelectionChange(toggleRowSelection(selection, rowId))
  }

  const handleColumnCheckBoxChange = (
    columns,
    setColumns,
    colDef,
    defaultColumns
  ) => {
    const newColumns = columns.map((column) => {
      if (column.key === colDef.key) {
        return { ...column, hide: !column.hide }
      }
      return column
    })

    newColumns.sort((a, b) => {
      const aIndex = defaultColumns.findIndex((column) => column.key === a.key)
      const bIndex = defaultColumns.findIndex((column) => column.key === b.key)
      return aIndex - bIndex
    })

    setColumns(newColumns)
  }

  const renderColumnCheckBoxes = () => (
    <div className="w-min">
      <OverflowMenu variant="tableRow" icon="gear text-lg" iconStyle="solid">
        <div className="flex flex-col space-y-4 p-2">
          <span className="font-semibold">Table Columns</span>
          {colDefs.map((colDef) => (
            <FormCheckbox
              key={colDef.key}
              label={colDef.header}
              name={colDef.key}
              checked={columns.some(
                (column) => column.key === colDef.key && !column.hide
              )}
              onChange={() =>
                handleColumnCheckBoxChange(columns, setColumns, colDef, colDefs)
              }
            />
          ))}
        </div>
      </OverflowMenu>
    </div>
  )

  const renderActions = (actions) => {
    if (actions) {
      return (
        <div className="w-min">
          <OverflowMenu variant="tableRow">
            {actions.map((action, index) => (
              <OverflowMenu.Item
                key={index}
                onClick={action.onClick}
                variant={action.variant}
                href={action.href}
              >
                {action.action}
              </OverflowMenu.Item>
            ))}
          </OverflowMenu>
        </div>
      )
    } else {
      return <div />
    }
  }

  return (
    <>
      <div
        className={classNames({
          "overflow-x-auto": !fullHeight,
          "h-full overflow-y-visible": fullHeight,
        })}
        data-design-system="Table"
      >
        <table
          className={classNames("relative rounded border", {
            "table-auto": autoColumnWidth,
            "table-fixed": !autoColumnWidth,
            "w-full": !maxTableWidth,
            "w-max": maxTableWidth,
            "is-infinite group border-separate border-spacing-0 rounded-none border-none":
              infinite,
          })}
        >
          {/* Colgroup maintains column widths even when header uses colSpan */}
          <colgroup>
            {isSelectionEnabled && <col style={{ width: "50px" }} />}
            {columns.map((colDef, index) =>
              colDef.hide ? null : (
                <col
                  key={`col-${colDef.key}-${index}`}
                  style={{ width: colDef.width }}
                />
              )
            )}
            {(!hideSettings || !hideActions) && (
              <col style={{ width: "60px" }} />
            )}
          </colgroup>
          <Table.Head>
            <DataTableHeadRow
              isSelectionEnabled={isSelectionEnabled}
              allPageRowsSelected={allPageRowsSelected}
              selectedCount={selectedCount}
              totalSelectableCount={totalSelectableCount}
              pageSelectableCount={pageSelectableIds.length}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              onSelectAllAcrossPages={handleSelectAllAcrossPages}
              showSelectAllBanner={showSelectAllBanner}
              selectionActions={selectionActions}
              selection={selection}
              hideSettings={hideSettings}
              hideActions={hideActions}
              renderColumnCheckBoxes={renderColumnCheckBoxes}
              visibleColumnsCount={visibleColumnsCount}
            >
              {columns.map((colDef, index) => {
                if (colDef.hide) {
                  return null
                }
                return (
                  <Table.Head.Cell
                    key={`${colDef.key}-${index}`}
                    columnWidth={colDef.width}
                    onClick={
                      colDef.sortable && sortConfig.onColumnSort
                        ? () => sortConfig.onColumnSort(colDef.key)
                        : null
                    }
                    sortDirection={sortConfig?.sortDirection}
                    isSortColumn={sortConfig?.sortKey === colDef.key}
                  >
                    {colDef.header}
                  </Table.Head.Cell>
                )
              })}
            </DataTableHeadRow>
          </Table.Head>
          <Table.Body
            showBlankRows={showBlankRows}
            isLoading={isLoading}
            rowCount={rowCount}
          >
            {rows?.map((row) => {
              const { actions, color, textColor, variant, onClick, innerRef } =
                rowConfig || {}

              const rowIsSelectable =
                isSelectionEnabled && isRowSelectableFn(row)
              const rowIsSelected =
                isSelectionEnabled && isRowSelected(selection, row.id)

              return (
                <Table.Row
                  key={row.id}
                  selectable={rowIsSelectable}
                  selectableDisabled={isSelectionEnabled && !rowIsSelectable}
                  isSelected={rowIsSelected}
                  onSelect={() => handleToggleRowSelection(row.id)}
                  innerRef={innerRef ? innerRef(row) : null}
                  onClick={onClick ? () => onClick(row) : null}
                  textColor={textColor ? textColor(row) : "default"}
                  rowColor={color ? color(row) : "default"}
                  variant={variant ? variant(row) : "default"}
                >
                  {columns.map((colDef, colIndex) => {
                    const cellData = row[colDef.key]
                    if (colDef.hide) {
                      return null
                    }
                    return (
                      <Table.Cell
                        key={`${colDef.key}-${colIndex}`}
                        align={colDef.cellAlign}
                        columnWidth={colDef.width}
                      >
                        {colDef.render ? colDef.render(row) : cellData}
                      </Table.Cell>
                    )
                  })}
                  {!hideActions ? (
                    <Table.Cell align="right">
                      {actions ? renderActions(actions(row)) : <div />}
                    </Table.Cell>
                  ) : (
                    !hideSettings && (
                      <Table.Cell>
                        <div />
                      </Table.Cell>
                    )
                  )}
                </Table.Row>
              )
            })}
          </Table.Body>
        </table>
      </div>
      {pagination && numberOfPages > 1 ? (
        <div className="mt-2">
          <Pagination
            numberOfPages={numberOfPages}
            onPageChange={onPageChange}
            page={page}
          />
        </div>
      ) : null}
    </>
  )
}

DataTable.propTypes = {
  autoColumnWidth: PropTypes.bool,
  colDefs: PropTypes.arrayOf(
    PropTypes.shape({
      cellAlign: PropTypes.oneOf(["left", "right", "center", "justify"]),
      header: PropTypes.string.isRequired,
      hide: PropTypes.bool,
      key: PropTypes.string.isRequired,
      render: PropTypes.func,
      width: PropTypes.string,
    })
  ),
  fullHeight: PropTypes.bool,
  hideActions: PropTypes.bool,
  hideSettings: PropTypes.bool,
  infinite: PropTypes.bool,
  isLoading: PropTypes.bool,
  maxTableWidth: PropTypes.bool,
  name: PropTypes.string.isRequired,
  numberOfPages: PropTypes.number,
  onPageChange: PropTypes.func,
  page: PropTypes.number,
  pagination: PropTypes.bool,
  rowConfig: PropTypes.shape({
    actions: PropTypes.func,
    color: PropTypes.func,
    innerRef: PropTypes.func,
    textColor: PropTypes.func,
    variant: PropTypes.func,
    onClick: PropTypes.func,
  }),
  rowCount: PropTypes.number,
  rowData: PropTypes.arrayOf(PropTypes.object.isRequired),
  selectable: SelectablePropType,
  sortConfig: PropTypes.shape({
    sortDirection: PropTypes.oneOf(["asc", "desc", "ASC", "DESC"]),
    sortKey: PropTypes.string,
    onColumnSort: PropTypes.func,
  }),
  showBlankRows: PropTypes.bool,
}

export default DataTable
